/**
 * Custom Hooks
 */

import React, { useCallback, useEffect } from 'react';
import { useAuthStore } from '@/store/auth.store';
import { NavItem } from '@/types';

/**
 * Hook to check if user is authenticated
 */
export const useAuth = () => {
  return useAuthStore((state) => ({
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    isInitialized: state.isInitialized,
    isLoading: state.isLoading,
    error: state.error,
    login: state.login,
    logout: state.logout,
    register: state.register,
  }));
};

/**
 * Hook to initialize auth on app load
 */
export const useInitializeAuth = () => {
  const initialize = useAuthStore((state) => state.initialize);

  useEffect(() => {
    initialize();
  }, [initialize]);
};

/**
 * Hook to check if user has permission
 */
export const useHasPermission = (permission: string) => {
  const user = useAuthStore((state) => state.user);
  return user?.permissions?.includes(permission) ?? false;
};

/**
 * Hook to check if user has role
 */
export const useHasRole = (role: string) => {
  const user = useAuthStore((state) => state.user);
  return user?.role === role;
};

/**
 * Hook for debounced value
 */
export const useDebouncedValue = <T,>(value: T, delay: number = 500): T => {
  const [debouncedValue, setDebouncedValue] = React.useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

/**
 * Hook for previous value
 */
export const usePrevious = <T,>(value: T): T | undefined => {
  const ref = React.useRef<T>();

  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
};

/**
 * Hook for local storage
 */
export const useLocalStorage = <T,>(
  key: string,
  initialValue: T
): [T, (value: T) => void] => {
  const [storedValue, setStoredValue] = React.useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(error);
      return initialValue;
    }
  });

  const setValue = useCallback(
    (value: T) => {
      try {
        setStoredValue(value);
        window.localStorage.setItem(key, JSON.stringify(value));
      } catch (error) {
        console.error(error);
      }
    },
    [key]
  );

  return [storedValue, setValue];
};

/**
 * Hook for async state
 */
export const useAsync = <T,>(
  asyncFunction: () => Promise<T>,
  immediate: boolean = true
) => {
  const [status, setStatus] = React.useState<'idle' | 'pending' | 'success' | 'error'>('idle');
  const [data, setData] = React.useState<T | null>(null);
  const [error, setError] = React.useState<Error | null>(null);

  const execute = useCallback(async () => {
    setStatus('pending');
    setData(null);
    setError(null);

    try {
      const response = await asyncFunction();
      setData(response);
      setStatus('success');
      return response;
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      setStatus('error');
      throw err;
    }
  }, [asyncFunction]);

  useEffect(() => {
    if (immediate) {
      execute();
    }
  }, [execute, immediate]);

  return { execute, status, data, error };
};


// ─── useNavAccess ─────────────────────────────────────────────────────────────

export const useNavAccess = () => {
  const user = useAuthStore((state) => state.user);

  const canAccess = (item: NavItem): boolean => {
    if (!item.roles?.length && !item.permissions?.length) return true;
    if (item.roles?.length && !item.roles.includes(user?.role ?? '')) return false;
    if (item.permissions?.length) {
      const userPerms: string[] = user?.permissions ?? [];
      if (!item.permissions.some((p) => userPerms.includes(p))) return false;
    }
    return true;
  };

  return { canAccess };
}